import { message } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';

const useWebSocket = (url, reconnectInterval = 5000) => {
  const [ws, setWs] = useState(null);
  const [connected, setConnected] = useState(false);
  const [messages, setMessages] = useState({});
  const isWaitingForResponseRef = useRef({});
  const reconnectTimeout = useRef(null);

  useEffect(() => {
    let socket;

    const connectWebSocket = () => {
      socket = new WebSocket(url);
      setWs(socket);

      socket.onopen = () => {
        setConnected(true);
        console.info('Connected to WebSocket server');
        if (reconnectTimeout.current) {
          clearTimeout(reconnectTimeout.current);
        }
      };

      socket.onclose = (event) => {
        setConnected(false);
        console.info(
          `Disconnected from WebSocket server. Code: ${event.code}, Reason: ${event.reason}`
        );
        // Implement reconnection logic
        if (!reconnectTimeout.current) {
          reconnectTimeout.current = setTimeout(() => {
            connectWebSocket();
          }, reconnectInterval);
        }
      };

      socket.onerror = (error) => {
        console.error('WebSocket error:', error);
        socket.close(); // Close the connection on error
      };

      socket.onmessage = (event) => {
        try {
          const parsedMessage = JSON.parse(event.data);
          setMessages(parsedMessage);
          console.info('Message received:', parsedMessage);
          const { collectionID, documentID } = parsedMessage;
          isWaitingForResponseRef.current[collectionID || documentID] = false;
          if (parsedMessage.error) message.error(`failed to send message: ${parsedMessage.error}`);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
    };

    connectWebSocket();

    return () => {
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      if (socket && socket.readyState === WebSocket.OPEN) {
        console.info('unmount/clean up - Closing WebSocket connection');
        socket.close();
      }
    };
  }, [url, reconnectInterval]);

  const sendMessage = useCallback(
    (messageData) => {
      const { collectionID, documentID } = messageData;
      if (ws && ws.readyState === WebSocket.OPEN) {
        console.info('Sending message:', messageData);
        isWaitingForResponseRef.current[collectionID || documentID] = true;
        ws.send(JSON.stringify(messageData));
      }
    },
    [ws]
  );

  return {
    messages,
    connected,
    sendMessage,
    isWaitingForResponse: isWaitingForResponseRef.current,
  };
};

export default useWebSocket;
